import { FrameEndTime } from 'owa-fps-types';

let internalState: { buffer: FrameEndTime[]; length: number } = { buffer: [], length: 0 };

/**
 * Add a new FrameEndTime.
 * @param time The timestamp of the endtime of the current frame
 * @param isDocumentVisible Whether the document was visible during this frame
 */
export function addFrameEndTime(time: number, isDocumentVisible: boolean) {
    // Reuse the existing object if it exists; otherwise, allocate one. Since we
    // recycle objects to avoid GCs, we'll end up only allocating new frames
    // when we have a minute that's more performant than any other prior minute
    // in this session
    internalState.buffer[internalState.length] = internalState.buffer[internalState.length] || {
        time: null,
        isNextFrameBackground: false,
    };

    internalState.buffer[internalState.length].isNextFrameBackground = isDocumentVisible;
    internalState.buffer[internalState.length].time = time;

    internalState.length++;

    // return the new FrameEndTime, so that it can be used for other
    // calculations, e.g. jank monitoring
    return internalState.buffer[internalState.length - 1];
}

/**
 * Clear the buffer.
 */
export function flushFrameEndTimes() {
    if (internalState.length > 1) {
        // since we want to calculate the deltas between each element of this array,
        // we should keep the last element of the previous minute, so that we have a starting
        // point for the next minute
        const last = internalState.buffer[internalState.length - 1];
        internalState.buffer[0].isNextFrameBackground = last.isNextFrameBackground;
        internalState.buffer[0].time = last.time;

        // Set the length back to 1, so that we continue adding from the right
        // point in the array.
        internalState.length = 1;
    }
}

/**
 * Return the buffer and the current length.
 *
 * **WARNING:** because the buffer is recycled, it may contain garbage at
 * indices after the current length
 */
export function getFrameEndTimes() {
    //
    return internalState;
}

/**
 * Get the first FrameEndTime of the buffer.
 */
export function getFirstFrameEndTime() {
    return internalState.buffer[0];
}

/**
 * Get the last FrameEndTime of the buffer.
 *
 * **WARNING:** Because the buffer is recycled and thus may contain garbage, the
 * last FrameEndTime is not the same as the last element in the buffer.
 */
export function getLastFrameEndTime() {
    return internalState.buffer[internalState.length - 1];
}

/**
 * Set the buffer to a new value.
 *
 * Used primarily for testing, as it causes a GC.
 * @param resetValue The value to use to set the buffer
 */
export function resetFrameEndTimes(resetValue: FrameEndTime[] = []) {
    internalState.buffer = resetValue;
    internalState.length = resetValue.length;
}

export type FrameEndTimeBuffer = typeof internalState;
